// src/components/SingleCell/SingleCellPage.jsx
import React, { useState } from "react";

export default function SingleCellPage() {
  const [metaFile, setMetaFile] = useState(null);
  const [exprFile, setExprFile] = useState(null);
  const [cellType, setCellType] = useState("");
  const [mlMethod, setMlMethod] = useState("all_models");
  const [folds, setFolds] = useState(3);
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState(null);
  const [err, setErr] = useState(null);
  const [usingSample, setUsingSample] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);

  const MODEL_OPTIONS = [
    { value: "logreg", label: "Logistic Regression" },
    { value: "random_forest", label: "Random Forest" },
    { value: "svm_linear", label: "Support Vector Machines (SVM)" },
    { value: "decision_tree", label: "Decision Tree" },
    { value: "cnn", label: "Convolutional Neural Networks" },
    { value: "all_models", label: "All Models (LR + RF + SVM + DT + CNN)" },
  ];

  const FOLD_OPTIONS = [2, 3, 4, 5];

  const SAMPLE_OPTIONS = [
    {
      value: "luad_small_pair",
      label: "small sample: meta.csv + small_expression.csv",
      meta: "meta.csv",
      expression: "small_expression.csv",
    },
  ];

  const [selectedSample, setSelectedSample] = useState("luad_small_pair");

  const sampleByValue = (value) => SAMPLE_OPTIONS.find((x) => x.value === value) || SAMPLE_OPTIONS[0];

  const readResponsePayload = async (response) => {
    const body = await response.text();
    if (!body) return {};

    try {
      return JSON.parse(body);
    } catch {
      return { message: body.trim() };
    }
  };

  const handleMetaFile = (e) => {
    const f = e.target.files?.[0] || null;
    setMetaFile(f);
    setUsingSample(false);
    setResp(null);
    setErr(null);
  };

  const handleExpressionFile = (e) => {
    const f = e.target.files?.[0] || null;
    setExprFile(f);
    setUsingSample(false);
    setResp(null);
    setErr(null);
  };

  const fetchSampleFile = async (filename) => {
    // IMPORTANT:
    // Do not load sample files directly from /files/sample/... first.
    // On the production server, /files may be mounted with a different BASE_DIR
    // or blocked by nginx/static routing. The API endpoint resolves the real
    // backend_singlecell/sample path and returns the CSV file.
    const urls = [
      `/api/single-cell/sample/${encodeURIComponent(filename)}`,
      `/api/single-cell/sample?filename=${encodeURIComponent(filename)}`,
      `/files/sample/${encodeURIComponent(filename)}`,
    ];

    let lastMessage = "";

    for (const sampleUrl of urls) {
      try {
        const r = await fetch(sampleUrl, { cache: "no-store" });
        if (r.ok) {
          const blob = await r.blob();
          return new File([blob], filename, { type: "text/csv" });
        }

        const data = await readResponsePayload(r);
        const detail = data?.error || data?.detail || data?.message || "";

        lastMessage = `${sampleUrl} returned HTTP ${r.status}${detail ? `: ${detail}` : ""}`;
      } catch (e) {
        lastMessage = `${sampleUrl} failed: ${e?.message || e}`;
      }
    }

    throw new Error(`Sample not found: ${filename}. ${lastMessage}`);
  };

  const onUseSample = async () => {
    try {
      setLoadingSample(true);
      setErr(null);
      setResp(null);

      const sample = sampleByValue(selectedSample);
      const [mf, ef] = await Promise.all([
        fetchSampleFile(sample.meta),
        fetchSampleFile(sample.expression),
      ]);

      setMetaFile(mf);
      setExprFile(ef);
      setUsingSample(true);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoadingSample(false);
    }
  };

  const onTrain = async () => {
    try {
      if (!metaFile || !exprFile) {
        throw new Error("Please choose both metadata and expression CSV files, or click 'Use sample CSVs'.");
      }

      setBusy(true);
      setErr(null);
      setResp(null);

      const fd = new FormData();
      fd.append("meta_file", metaFile);
      fd.append("expression_file", exprFile);
      fd.append("method", mlMethod);
      fd.append("n_folds", String(folds));
      if (cellType.trim()) fd.append("cell_type", cellType.trim());

      const r = await fetch("/api/single-cell/train", {
        method: "POST",
        body: fd,
      });

      const j = await readResponsePayload(r);
      if (!r.ok) {
        throw new Error(j?.detail || j?.error || j?.message || `HTTP ${r.status}`);
      }

      setResp(j);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const openV3 = () => {
    if (!resp) return;

    const method = resp?.method || mlMethod;

    try {
      localStorage.setItem("scv3_method", method);
      const groups = (resp?.groups || []).map((g) => g?.group).filter(Boolean);
      localStorage.setItem("scv3_groups", JSON.stringify(groups));
    } catch {}

    const ts = Date.now();
    window.open(
      `/single-cell-v3?method=${encodeURIComponent(method)}&ts=${ts}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const th = {
    textAlign: "left",
    padding: "12px 14px",
    borderBottom: "1px solid #52779b",
    fontWeight: 700,
    whiteSpace: "nowrap",
    color: "#f8fafc",
  };

  const td = {
    padding: "12px 14px",
    borderBottom: "1px solid #314c67",
    verticalAlign: "top",
    color: "#e5edf6",
  };

  const formatCounts = (counts) => {
    if (!counts || !Object.keys(counts).length) return "";
    return Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(", ");
  };

  return (
    <div
      className="single-cell-page"
      style={{ maxWidth: 1100, margin: "0 auto" }}
    >
      <style>{`
        .single-cell-page {
          --sc-surface: #102438;
          --sc-surface-raised: #16314a;
          --sc-surface-control: #0c1f31;
          --sc-border: #52779b;
          --sc-border-soft: #314c67;
          --sc-text: #f8fafc;
          --sc-text-secondary: #cbd5e1;
          --sc-accent: #60a5fa;
          --sc-accent-strong: #2563eb;
          box-sizing: border-box;
          padding: clamp(12px, 2.5vw, 24px);
          color: var(--sc-text);
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 16px;
          line-height: 1.6;
          text-align: left;
        }
        .single-cell-page *,
        .single-cell-page *::before,
        .single-cell-page *::after {
          box-sizing: border-box;
        }
        .single-cell-page .sc-form-grid,
        .single-cell-page .sc-help-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }
        .single-cell-page .sc-form-grid {
          align-items: end;
          margin-bottom: 16px;
        }
        .single-cell-page .sc-title {
          margin: 8px 0 6px;
          color: #ffffff;
          font-size: clamp(1.65rem, 2.5vw, 2.15rem);
          font-weight: 750;
          line-height: 1.2;
          letter-spacing: -0.02em;
        }
        .single-cell-page .sc-intro {
          max-width: 900px;
          margin: 0 0 20px;
          font-size: 1rem;
          line-height: 1.7;
        }
        .single-cell-page h3,
        .single-cell-page h4 {
          color: #ffffff;
          line-height: 1.35;
        }
        .single-cell-page li + li {
          margin-top: 5px;
        }
        .single-cell-page .sc-grid-full {
          grid-column: 1 / -1;
        }
        .single-cell-page .sc-inline-controls {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
          min-width: 0;
        }
        .single-cell-page .sc-sample-select {
          flex: 1 1 320px;
          min-width: 0;
          max-width: 100%;
        }
        .single-cell-page .sc-field-label {
          display: block;
          margin-bottom: 6px;
          color: #e7f2ff;
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.01em;
        }
        .single-cell-page .sc-field-control {
          width: 100%;
        }
        .single-cell-page .sc-file-input {
          max-width: 100%;
        }
        .single-cell-page .sc-progress {
          position: relative;
          width: 100%;
          height: 6px;
          background: #091827;
          border-radius: 6px;
          overflow: hidden;
        }
        .single-cell-page .sc-progress__bar {
          position: absolute;
          height: 100%;
          width: 35%;
          left: -35%;
          background: linear-gradient(90deg, #60a5fa, #93c5fd);
          border-radius: 6px;
          animation: single-cell-training-slide 1.4s ease-in-out infinite;
        }
        @keyframes single-cell-training-slide {
          0%   { left: -35%; }
          50%  { left: 50%; }
          100% { left: 100%; }
        }
        .single-cell-page .sc-card {
          background: linear-gradient(145deg, var(--sc-surface-raised), var(--sc-surface));
          color: var(--sc-text);
          border: 1px solid var(--sc-border-soft);
          border-radius: 12px;
          padding: clamp(16px, 2vw, 22px);
          box-shadow: 0 10px 24px rgba(3, 12, 22, 0.16);
        }
        .single-cell-page .sc-muted {
          color: var(--sc-text-secondary);
        }
        .single-cell-page .sc-btn {
          min-height: 42px;
          padding: 9px 18px;
          border-radius: 8px;
          border: 1px solid #7db7f3;
          background: var(--sc-accent-strong);
          color: #ffffff;
          font: inherit;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
        }
        .single-cell-page .sc-btn:disabled,
        .single-cell-page .sc-btn-secondary:disabled,
        .single-cell-page .sc-field-control:disabled,
        .single-cell-page .sc-file-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .single-cell-page .sc-btn-secondary {
          min-height: 42px;
          padding: 9px 16px;
          border-radius: 8px;
          border: 1px solid #6fa7df;
          background: #1e3a5f;
          color: #f8fafc;
          font: inherit;
          font-weight: 650;
          cursor: pointer;
          transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
        }
        .single-cell-page .sc-btn:not(:disabled):hover {
          background: #1d4ed8;
          border-color: #bfdbfe;
          transform: translateY(-1px);
        }
        .single-cell-page .sc-btn-secondary:not(:disabled):hover {
          background: #294f78;
          border-color: #bfdbfe;
          transform: translateY(-1px);
        }
        .single-cell-page .sc-field-control {
          min-height: 42px;
          background: var(--sc-surface-control);
          color: var(--sc-text);
          border: 1px solid var(--sc-border);
          border-radius: 8px;
          padding: 8px 10px;
          font: inherit;
        }
        .single-cell-page .sc-file-input {
          color: var(--sc-text-secondary);
          font: inherit;
          font-size: 0.9rem;
        }
        .single-cell-page .sc-file-input::file-selector-button {
          margin-right: 10px;
          padding: 8px 12px;
          border: 1px solid #6fa7df;
          border-radius: 7px;
          background: #1e3a5f;
          color: #ffffff;
          font: inherit;
          font-weight: 650;
          cursor: pointer;
        }
        .single-cell-page .sc-field-control:hover:not(:disabled),
        .single-cell-page .sc-file-input:hover:not(:disabled)::file-selector-button {
          border-color: #93c5fd;
        }
        .single-cell-page .sc-btn:focus-visible,
        .single-cell-page .sc-btn-secondary:focus-visible,
        .single-cell-page .sc-field-control:focus-visible,
        .single-cell-page .sc-file-input:focus-visible {
          outline: 3px solid #fbbf24;
          outline-offset: 3px;
        }
        .single-cell-page code {
          background: #091827;
          padding: 2px 6px;
          border-radius: 6px;
          color: #c9e3ff;
          font-size: 0.9em;
          overflow-wrap: anywhere;
        }
        .single-cell-page .sc-results-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--sc-surface);
          color: var(--sc-text);
          border: 1px solid var(--sc-border-soft);
          border-radius: 10px;
          overflow: hidden;
          font-size: 0.95rem;
        }
        .single-cell-page .sc-results-table thead tr {
          background: #1d4161;
        }
        .single-cell-page .sc-results-table tbody tr:nth-child(even) {
          background: rgba(96, 165, 250, 0.055);
        }
        .single-cell-page .sc-results-table tbody tr:hover {
          background: rgba(96, 165, 250, 0.12);
        }
        .single-cell-page .sc-error {
          margin-top: 12px;
          padding: 12px 14px;
          border: 1px solid #fca5a5;
          border-radius: 9px;
          background: #4c1d24;
          color: #fee2e2;
        }
        @media (max-width: 800px) {
          .single-cell-page .sc-form-grid,
          .single-cell-page .sc-help-grid {
            grid-template-columns: minmax(0, 1fr);
          }
          .single-cell-page .sc-grid-full {
            grid-column: 1;
          }
        }
        @media (max-width: 480px) {
          .single-cell-page .sc-inline-controls {
            align-items: stretch;
            flex-direction: column;
          }
          .single-cell-page .sc-inline-controls > * {
            width: 100%;
          }
          .single-cell-page .sc-sample-select {
            flex-basis: auto;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .single-cell-page .sc-progress__bar {
            animation: none;
            left: 0;
            width: 100%;
          }
        }
      `}</style>

      <h2 className="sc-title">Single-Cell Training</h2>
      <p className="sc-muted sc-intro">
        Upload separated scRNA-seq metadata and expression CSV files. The server merges them by <code>barcode</code>,
        generates labels from <code>Tissue</code>, partitions by <code>Gender × cell_type</code>,
        creates stratified 80/10/10 folds, and trains models per group.
      </p>

      <div className="sc-card sc-form-grid">
        <div>
          <label className="sc-field-label" htmlFor="sc-meta-file"><b>Metadata CSV</b></label>
          <div className="sc-inline-controls">
            <input
              id="sc-meta-file"
              className="sc-file-input"
              type="file"
              accept=".csv"
              onChange={handleMetaFile}
              disabled={busy || loadingSample}
            />
            <span className="sc-muted" style={{ fontSize: 13 }}>
              {metaFile ? <code>{metaFile.name}</code> : "No metadata file chosen"}
            </span>
          </div>
        </div>

        <div>
          <label className="sc-field-label" htmlFor="sc-expression-file"><b>Expression CSV</b></label>
          <div className="sc-inline-controls">
            <input
              id="sc-expression-file"
              className="sc-file-input"
              type="file"
              accept=".csv"
              onChange={handleExpressionFile}
              disabled={busy || loadingSample}
            />
            <span className="sc-muted" style={{ fontSize: 13 }}>
              {exprFile ? <code>{exprFile.name}</code> : "No expression file chosen"}
            </span>
          </div>
        </div>

        <div className="sc-grid-full">
          <label className="sc-field-label" htmlFor="sc-sample-select"><b>Sample CSVs</b></label>
          <div className="sc-inline-controls">
            <select
              id="sc-sample-select"
              className="sc-field-control sc-sample-select"
              value={selectedSample}
              onChange={(e) => setSelectedSample(e.target.value)}
              title="Select bundled sample input files"
              disabled={busy || loadingSample}
            >
              {SAMPLE_OPTIONS.map((sample) => (
                <option key={sample.value} value={sample.value}>
                  {sample.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="sc-btn-secondary"
              onClick={onUseSample}
              title="Load selected metadata and expression sample files"
              disabled={busy || loadingSample}
            >
              {loadingSample ? "Loading sample…" : "Use sample CSVs"}
            </button>

            {loadingSample && (
              <span className="sc-muted" role="status" aria-live="polite" style={{ fontSize: 13 }}>
                Loading metadata and expression sample files…
              </span>
            )}

            {usingSample && !loadingSample && (
              <span className="sc-muted" role="status" aria-live="polite" style={{ fontSize: 13 }}>
                Using sample pair: <code>{metaFile?.name}</code> + <code>{exprFile?.name}</code>
              </span>
            )}
          </div>
        </div>

        <div>
          <label className="sc-field-label" htmlFor="sc-ml-method"><b>ML method (driver)</b></label>
          <select
            id="sc-ml-method"
            className="sc-field-control"
            value={mlMethod}
            onChange={(e) => setMlMethod(e.target.value)}
            disabled={busy || loadingSample}
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="sc-field-label" htmlFor="sc-folds"><b>Folds</b></label>
          <select
            id="sc-folds"
            className="sc-field-control"
            value={folds}
            onChange={(e) => setFolds(Number(e.target.value))}
            disabled={busy || loadingSample}
          >
            {FOLD_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="sc-field-label" htmlFor="sc-cell-type"><b>Cell type (optional)</b></label>
          <input
            id="sc-cell-type"
            className="sc-field-control"
            type="text"
            value={cellType}
            onChange={(e) => setCellType(e.target.value)}
            placeholder='e.g., "ATII" or leave blank for all'
            disabled={busy || loadingSample}
          />
        </div>

        <div className="sc-muted" style={{ fontSize: 13, alignSelf: "center" }}>
          Metadata and expression are matched using <code>barcode</code>. The backend creates <code>label</code> from <code>Tissue</code>:
          Tumor → lst, Normal → non_lst.
        </div>

        <div className="sc-grid-full">
          <button
            onClick={onTrain}
            type="button"
            disabled={busy || loadingSample || !metaFile || !exprFile}
            className="sc-btn"
            style={{ marginRight: 12 }}
          >
            {busy ? "Training…" : "Train"}
          </button>

          {resp && (
            <button type="button" onClick={openV3} className="sc-btn-secondary">
              Open results in v3
            </button>
          )}
        </div>

        {busy && (
          <div className="sc-grid-full" role="status" aria-live="polite">
            <div className="sc-progress" aria-hidden="true">
              <div className="sc-progress__bar" />
            </div>
            <div className="sc-muted" style={{ marginTop: 6, fontSize: 13 }}>
              This may take a minute; we’re merging inputs, creating folds, and training per-group models…
            </div>
          </div>
        )}
      </div>

      {err && (
        <div className="sc-error" role="alert" aria-live="assertive">
          <b>Error:</b> {err}
        </div>
      )}

      {resp?.status === "ok" && (
        <div className="sc-card" role="status" aria-live="polite" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 18, marginBottom: 6 }}>
            {resp?.message || "Training complete."}{" "}
            <span style={{ color: "#93c5fd" }}>
              (method: {resp?.method ?? mlMethod})
            </span>
          </div>

          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 8 }}>
            <div><b>Groups trained:</b> {resp?.totals?.groups_trained ?? "–"}</div>
            <div><b>Models trained:</b> {resp?.totals?.models_trained ?? "–"}</div>
            <div><b>Metadata rows:</b> {resp?.dataset_summary?.metadata_rows ?? "–"}</div>
            <div><b>Expression rows:</b> {resp?.dataset_summary?.expression_rows ?? "–"}</div>
            <div><b>Merged rows:</b> {resp?.dataset_summary?.merged_rows ?? "–"}</div>
          </div>

          {resp?.biomarkers && (
            <div className="sc-muted" style={{ fontSize: 13 }}>
              <b>Biomarker extraction:</b>{" "}
              {resp.biomarkers.status}
              {resp.biomarkers.error ? ` — ${resp.biomarkers.error}` : ""}
            </div>
          )}
        </div>
      )}

      {resp?.status === "ok" && (
        <div style={{ marginTop: 12, overflowX: "auto" }}>
          <table className="sc-results-table">
            <thead>
              <tr>
                <th style={th}>Group</th>
                <th style={th}>Gender</th>
                <th style={th}>Cell type</th>
                <th style={th}>Class counts</th>
                <th style={th}>Folds</th>
              </tr>
            </thead>
            <tbody>
              {(resp?.groups || []).map((g, idx) => {
                const foldsCount = g?.n_folds ?? g?.fold_indices?.length ?? "–";

                return (
                  <tr key={`${g?.group || "group"}-${idx}`}>
                    <td style={td}>{g?.group ?? ""}</td>
                    <td style={td}>{g?.gender ?? ""}</td>
                    <td style={td}>{g?.cell_type ?? ""}</td>
                    <td style={td}>{formatCounts(g?.class_counts)}</td>
                    <td style={td}>{foldsCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resp?.skipped_groups?.length > 0 && (
        <div className="sc-card" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>Skipped groups</div>
          <div className="sc-muted" style={{ fontSize: 14, marginBottom: 8 }}>
            These groups did not have enough class diversity or had extreme imbalance for training.
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="sc-results-table">
              <thead>
                <tr>
                  <th style={th}>Group</th>
                  <th style={th}>Gender</th>
                  <th style={th}>Cell type</th>
                  <th style={th}>Class counts</th>
                  <th style={th}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {resp.skipped_groups.map((g, idx) => (
                  <tr key={`${g?.group || "skipped"}-${idx}`}>
                    <td style={td}>{g?.group ?? ""}</td>
                    <td style={td}>{g?.gender ?? ""}</td>
                    <td style={td}>{g?.cell_type ?? ""}</td>
                    <td style={td}>{formatCounts(g?.class_counts)}</td>
                    <td style={td}>{g?.reason ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sc-card" style={{ marginTop: 18 }}>
        <h3 style={{ marginTop: 0 }}>How to prepare your scRNA-seq input</h3>
        <p className="sc-muted" style={{ marginTop: 4 }}>
          Upload two CSV files: one metadata file and one expression matrix. G2PDeep matches rows by <code>barcode</code>,
          creates the binary training label from <code>Tissue</code>, groups cells by <b>Gender × cell_type</b>,
          generates independent stratified 80/10/10 splits, and trains the selected machine learning model.
        </p>

        <div className="sc-help-grid" style={{ marginTop: 8 }}>
          <div>
            <h4 style={{ margin: "6px 0" }}>Required metadata columns</h4>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li><code>barcode</code> — unique cell barcode, matching the expression file</li>
              <li><code>Gender</code> — Male, Female, or Unknown</li>
              <li><code>cell_type</code> — cell annotation such as ATII, CD4, CD8, or Cilia</li>
              <li><code>Tissue</code> — Tumor or Normal; used to generate the model label</li>
            </ul>
          </div>

          <div>
            <h4 style={{ margin: "6px 0" }}>Required expression columns</h4>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li><code>barcode</code> — same barcode values as the metadata file</li>
              <li>Gene expression columns — one numeric column per gene</li>
            </ul>
            <p className="sc-muted" style={{ marginTop: 6 }}>
              The backend ignores metadata columns during model fitting and uses only numeric gene features.
            </p>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <h4 style={{ margin: "6px 0" }}>Notes</h4>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li><code>Tissue = Tumor</code> becomes <code>label = lst</code>; <code>Tissue = Normal</code> becomes <code>label = non_lst</code>.</li>
            <li>Groups with only one class or extreme imbalance are skipped and listed in the summary.</li>
            <li>The sample option loads server files <code>sample/meta.csv</code> and <code>sample/small_expression.csv</code>.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
